// AppsPage — list all bubble_apps for the tenant.
// Each row shows app_name, app_slug, last WU %, and last audit time.
// Clicking a row navigates to /apps/:appId/audits.

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppWindow, ChevronRight, AlertTriangle, Layers } from "lucide-react";
import { fetchApps, type App } from "../api";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";
import { EmptyState } from "@avagency/frontend/components/shell/EmptyState";
import { Footer } from "../components/Footer";

// WU percentage badge — red when at/over plan limit (>= 100)
function WUBadge({ pct }: { pct: number | null }) {
  if (pct == null) {
    return (
      <span
        className="text-xs"
        style={{ color: "hsl(218 14% 46%)" }}
      >
        —
      </span>
    );
  }
  const atRisk = pct >= 100;
  const isWarn = pct >= 80 && pct < 100;

  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5"
      style={{
        background: atRisk
          ? "hsl(0 100% 75% / 0.1)"
          : isWarn
          ? "hsl(41 73% 62% / 0.1)"
          : "hsl(217 91% 60% / 0.08)",
        color: atRisk
          ? "hsl(0 100% 75%)"
          : isWarn
          ? "hsl(41 73% 68%)"
          : "hsl(217 91% 68%)",
        border: `1px solid ${
          atRisk
            ? "hsl(0 100% 75% / 0.25)"
            : isWarn
            ? "hsl(41 73% 62% / 0.22)"
            : "hsl(217 91% 60% / 0.2)"
        }`,
      }}
    >
      {atRisk && (
        <AlertTriangle className="h-3 w-3" aria-label="At risk" />
      )}
      {pct}%
    </span>
  );
}

function AppRow({ app }: { app: App }) {
  const auditedAt = app.last_audited_at
    ? new Date(app.last_audited_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "Never";

  return (
    <Link
      to={`/apps/${app.id}/audits`}
      className="group flex items-center gap-4 rounded-xl border px-4 py-3.5 text-sm transition-surface"
      style={{
        background:
          "linear-gradient(180deg, hsl(223 55% 10% / 0.5), hsl(222 67% 5% / 0.7))",
        borderColor: "hsl(220 18% 85% / 0.1)",
      }}
    >
      {/* App icon */}
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-lg"
        style={{
          width: 36,
          height: 36,
          background: "hsl(217 91% 60% / 0.1)",
          border: "1px solid hsl(217 91% 60% / 0.2)",
          color: "hsl(217 91% 65%)",
        }}
        aria-hidden="true"
      >
        <AppWindow className="h-4 w-4" />
      </div>

      {/* Name + slug */}
      <div className="min-w-0 flex-1">
        <span
          className="block truncate font-medium"
          style={{ color: "hsl(220 18% 88%)" }}
        >
          {app.app_name}
        </span>
        <span
          className="block truncate text-xs mt-0.5"
          style={{
            color: "hsl(218 14% 50%)",
            fontFamily: "var(--font-mono, ui-monospace)",
          }}
        >
          {app.app_slug}
        </span>
      </div>

      {/* Metadata — visible at all sizes; column-stacked on very small screens */}
      <div className="flex items-center gap-3 sm:gap-5 flex-shrink-0">
        <div className="text-right">
          <div
            className="text-xs mb-1"
            style={{
              color: "hsl(218 14% 46%)",
              fontFamily: "var(--font-mono, ui-monospace)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontSize: "10px",
            }}
          >
            WU
          </div>
          <WUBadge pct={app.last_wu_pct} />
        </div>
        <div className="text-right min-w-[5rem]">
          <div
            className="text-xs mb-1"
            style={{
              color: "hsl(218 14% 46%)",
              fontFamily: "var(--font-mono, ui-monospace)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontSize: "10px",
            }}
          >
            Last audit
          </div>
          <span
            className="text-xs"
            style={{ color: "hsl(218 14% 60%)" }}
          >
            {auditedAt}
          </span>
        </div>
      </div>

      <ChevronRight
        className="h-4 w-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5"
        style={{ color: "hsl(218 14% 46%)" }}
        aria-hidden="true"
      />
    </Link>
  );
}

export function AppsPage() {
  const q = useQuery<App[]>({
    queryKey: ["bubble", "apps"],
    queryFn: () => fetchApps(),
  });

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1
          className="text-2xl font-semibold"
          style={{ letterSpacing: "-0.02em" }}
        >
          Apps
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Bubble apps connected through your integration.
        </p>
      </div>

      {q.isLoading && <LoadingState rows={4} />}

      {q.error && (
        <ErrorState
          error={q.error}
          retry={() => q.refetch()}
          description="Could not load your Bubble apps. Check your integration is active."
        />
      )}

      {q.data && q.data.length === 0 && (
        <EmptyState
          icon={<Layers className="h-10 w-10 opacity-50" />}
          title="No apps imported yet"
          description="Apps are imported automatically after your Bubble integration syncs. This usually takes under a minute. Refresh the page to check for updates."
        />
      )}

      {q.data && q.data.length > 0 && (
        <div className="flex flex-col gap-2">
          {q.data.map((a) => (
            <AppRow key={a.id} app={a} />
          ))}
        </div>
      )}

      <Footer />
    </div>
  );
}
